/**
 * TableView for Poker Reborn
 *
 * Renders the poker table felt and border as a rounded trapezoid,
 * giving a perspective effect from the player's viewpoint.
 *
 * @see docs/screens/room/TABLE.md - Visual layout, seat positions, dimensions
 * @see docs/screens/room/TABLE_STYLING_SHOWCASE.md - Styling options reference
 */

import { Container, Graphics, Texture, TilingSprite } from 'pixi.js';
import { addOriginMarker, ORIGIN_COLORS } from '@/utils/OriginDebug';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  /** Table border width */
  borderWidth: 20,

  /** Rail color */
  rail: 0x3d2817,

  /** Rail highlight */
  railHighlight: 0x5c3d24,

  /** Gold trim between rail and felt (B2) */
  goldTrim: {
    color: 0xc9a84c,
    width: 1.5,
    inset: 4,
  },

  /** Solid felt base color */
  felt: 0x0d5c3d,

  /** Fabric texture overlay (A2) */
  feltTexture: {
    tileSize: 64,
    alpha: 0.12,
  },

  /** Vignette — dark edges (A5) */
  vignette: {
    color: 0x000000,
    /** Concentric dark strokes from edge inward [inset, width, alpha] */
    layers: [
      [4, 28, 0.14],
      [28, 22, 0.09],
      [46, 16, 0.04],
    ] as readonly [number, number, number][],
  },

  /** Felt edge highlight */
  edgeHighlight: 0x1a7a4f,

  /** Ambient occlusion where felt meets rail (C1) */
  ambientOcclusion: {
    color: 0x031a0f,
    alpha: 0.55,
    width: 3,
    inset: 3,
  },

  /** Drop shadow beneath the table */
  shadow: {
    color: 0x050510,
    alpha: 0.35,
    offsetY: 8,
    blur: 18,
    layers: 5,
  },

  /** Trapezoid perspective: top edge is this fraction of bottom edge width */
  perspectiveRatio: 0.85,

  /** Outward bulge on left/right sides as fraction of total width */
  sideBulge: 0.015,

  /** Corner radius as fraction of table height */
  cornerRadiusFraction: 0.18,

  /** Top corners are this much larger than bottom corners */
  topCornerRadiusScale: 1.25,
};

// =============================================================================
// POLYLINE HELPERS
// =============================================================================

/** Number of line segments to approximate each curve/arc */
const CURVE_RESOLUTION = 16;

interface Point2D {
  x: number;
  y: number;
}

/**
 * Approximate a quadratic bezier curve as line segments.
 * B(t) = (1-t)²·start + 2(1-t)t·control + t²·end
 * Pushes points for t = 1/N, 2/N, ..., 1 (skips t=0, which is already in the array).
 */
function addQuadratic(
  points: Point2D[],
  start: Point2D,
  control: Point2D,
  end: Point2D
): void {
  for (let i = 1; i <= CURVE_RESOLUTION; i++) {
    const t = i / CURVE_RESOLUTION;
    const oneMinusT = 1 - t;
    points.push({
      x: oneMinusT * oneMinusT * start.x + 2 * oneMinusT * t * control.x + t * t * end.x,
      y: oneMinusT * oneMinusT * start.y + 2 * oneMinusT * t * control.y + t * t * end.y,
    });
  }
}

/**
 * Approximate a corner arc as line segments.
 * Computes the arc center from the tangent-in point, corner vertex, and tangent-out point,
 * then interpolates angles from start to end.
 * Pushes points for fractions 1/N through N/N (skips the start, which is already in the array).
 */
function addCornerArc(
  points: Point2D[],
  tangentIn: Point2D,
  cornerVertex: Point2D,
  tangentOut: Point2D,
  radius: number
): void {
  if (radius <= 0) {
    points.push(cornerVertex);
    return;
  }

  // Compute arc center: offset from corner vertex along the bisector
  const dx1 = tangentIn.x - cornerVertex.x;
  const dy1 = tangentIn.y - cornerVertex.y;
  const dx2 = tangentOut.x - cornerVertex.x;
  const dy2 = tangentOut.y - cornerVertex.y;

  // Bisector direction (unnormalized)
  const bisectX = dx1 + dx2;
  const bisectY = dy1 + dy2;
  const bisectLen = Math.sqrt(bisectX * bisectX + bisectY * bisectY);

  if (bisectLen < 0.001) {
    // Degenerate case: tangent points are opposite, just push the corner
    points.push(cornerVertex);
    return;
  }

  // Distance from corner vertex to arc center along bisector
  const halfAngle = Math.acos(
    Math.max(-1, Math.min(1,
      (dx1 * dx2 + dy1 * dy2) /
      (Math.sqrt(dx1 * dx1 + dy1 * dy1) * Math.sqrt(dx2 * dx2 + dy2 * dy2))
    ))
  ) / 2;

  if (halfAngle < 0.001) {
    points.push(cornerVertex);
    return;
  }

  const centerDist = radius / Math.sin(halfAngle);

  const centerX = cornerVertex.x + (bisectX / bisectLen) * centerDist;
  const centerY = cornerVertex.y + (bisectY / bisectLen) * centerDist;

  // Start and end angles
  const startAngle = Math.atan2(tangentIn.y - centerY, tangentIn.x - centerX);
  const endAngle = Math.atan2(tangentOut.y - centerY, tangentOut.x - centerX);

  // Ensure we take the short arc (the one that goes through the corner side)
  let angleDiff = endAngle - startAngle;
  if (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
  if (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

  for (let i = 1; i <= CURVE_RESOLUTION; i++) {
    const fraction = i / CURVE_RESOLUTION;
    const angle = startAngle + angleDiff * fraction;
    points.push({
      x: centerX + Math.cos(angle) * radius,
      y: centerY + Math.sin(angle) * radius,
    });
  }
}

// =============================================================================
// TABLE VIEW
// =============================================================================

export class TableView extends Container {
  private tableGraphics: Graphics;
  private feltMask: Graphics;
  private feltTextureSprite: TilingSprite;
  private tableWidth: number;
  private tableHeight: number;

  constructor(width: number, height: number) {
    super();

    this.tableWidth = width;
    this.tableHeight = height;

    // Base graphics layer (shadow, rail, felt, strokes)
    this.tableGraphics = new Graphics();
    this.addChild(this.tableGraphics);

    // Fabric texture overlay (A2) — masked to felt shape
    this.feltMask = new Graphics();
    this.addChild(this.feltMask);

    const noiseTexture = this.createNoiseTexture(STYLES.feltTexture.tileSize);
    this.feltTextureSprite = new TilingSprite({
      texture: noiseTexture,
      width,
      height,
    });
    this.feltTextureSprite.alpha = STYLES.feltTexture.alpha;
    this.feltTextureSprite.mask = this.feltMask;
    this.addChild(this.feltTextureSprite);

    this.draw();

    // Debug: Add origin marker at table center if ?showOrigins=true
    // Note: TableView origin (0,0) is at top-left; marker shows visual center
    const marker = addOriginMarker(this, ORIGIN_COLORS.table);
    if (marker) {
      marker.x = this.tableWidth / 2;
      marker.y = this.tableHeight / 2;
    }
  }

  /**
   * Resize the table
   */
  resize(width: number, height: number): void {
    this.tableWidth = width;
    this.tableHeight = height;
    this.feltTextureSprite.width = width;
    this.feltTextureSprite.height = height;
    this.draw();
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private draw(): void {
    this.tableGraphics.clear();

    const centerX = this.tableWidth / 2;
    const centerY = this.tableHeight / 2;

    // Table dimensions
    const tableW = this.tableWidth * 0.75;
    const tableH = this.tableHeight * 0.55;
    const borderW = STYLES.borderWidth;

    // 1. Drop shadow (multi-layer blur beneath the table)
    const { shadow } = STYLES;
    const shadowCY = centerY + shadow.offsetY;

    for (let i = shadow.layers; i >= 0; i--) {
      const expand = shadow.blur * (i / shadow.layers);
      const alpha = shadow.alpha * ((shadow.layers - i) / shadow.layers);
      this.drawTrapezoidFill(
        centerX,
        shadowCY,
        tableW + borderW * 2 + expand * 2,
        tableH + borderW * 2 + expand * 2,
        { color: shadow.color, alpha }
      );
    }

    // 2. Outer rail (wood border)
    this.drawTrapezoidFill(
      centerX,
      centerY,
      tableW + borderW * 2,
      tableH + borderW * 2,
      STYLES.rail
    );

    // 3. Rail highlight
    this.drawTrapezoidStroke(
      centerX,
      centerY,
      tableW + borderW * 2 - 6,
      tableH + borderW * 2 - 6,
      STYLES.railHighlight,
      2
    );

    // 4. Gold trim (B2) — thin accent between rail and felt
    const trimInset = STYLES.goldTrim.inset;
    this.drawTrapezoidStroke(
      centerX,
      centerY,
      tableW + trimInset,
      tableH + trimInset,
      STYLES.goldTrim.color,
      STYLES.goldTrim.width
    );

    // 5. Solid felt fill
    this.drawTrapezoidFill(centerX, centerY, tableW, tableH, STYLES.felt);

    // 6. Vignette (A5) — dark semi-transparent strokes at felt edges
    for (const [inset, width, vignetteAlpha] of STYLES.vignette.layers) {
      this.drawTrapezoidStroke(
        centerX,
        centerY,
        tableW - inset,
        tableH - inset,
        STYLES.vignette.color,
        width,
        vignetteAlpha
      );
    }

    // 7. Ambient occlusion (C1) — dark shadow where felt meets rail
    const ao = STYLES.ambientOcclusion;
    this.drawTrapezoidStroke(
      centerX,
      centerY,
      tableW - ao.inset,
      tableH - ao.inset,
      ao.color,
      ao.width,
      ao.alpha
    );

    // 8. Felt edge highlight — crisp bright border on felt
    this.drawTrapezoidStroke(
      centerX,
      centerY,
      tableW - 10,
      tableH - 10,
      STYLES.edgeHighlight,
      2
    );

    // 9. Update felt texture mask to match current felt shape
    this.feltMask.clear();
    this.drawTrapezoidFillOn(this.feltMask, centerX, centerY, tableW, tableH, 0xffffff);
  }

  // ===========================================================================
  // FELT TEXTURE (A2)
  // ===========================================================================

  /**
   * Create a small noise texture that tiles seamlessly to simulate fabric weave.
   * Combines a subtle checkerboard pattern with random noise.
   */
  private createNoiseTexture(size: number): Texture {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d');
    if (!ctx) return Texture.EMPTY;

    const imageData = ctx.createImageData(size, size);
    const data = imageData.data;

    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const idx = (y * size + x) * 4;
        // Subtle checkerboard for woven feel + random noise for grain
        const weave = ((x + y) % 2 === 0) ? 8 : -8;
        const noise = (Math.random() - 0.5) * 25;
        const value = Math.max(0, Math.min(255, 128 + weave + noise));

        data[idx] = value;
        data[idx + 1] = value;
        data[idx + 2] = value;
        data[idx + 3] = 255;
      }
    }

    ctx.putImageData(imageData, 0, 0);
    return Texture.from(canvas);
  }

  // ===========================================================================
  // TRAPEZOID DRAWING
  // ===========================================================================

  /**
   * Draw a rounded trapezoid path on tableGraphics.
   * Wider at the bottom (player side), narrower at the top (perspective).
   */
  private trapezoidPath(
    cx: number,
    cy: number,
    totalWidth: number,
    totalHeight: number
  ): void {
    this.buildTrapezoidPath(this.tableGraphics, cx, cy, totalWidth, totalHeight);
  }

  /**
   * Build a rounded trapezoid path on any Graphics object.
   * All four edges curve slightly outward for a softer, barrel-like shape.
   *
   * Uses pure moveTo/lineTo polyline (no arcTo/quadraticCurveTo) to avoid
   * triangle artifacts on mobile GPUs.
   */
  private buildTrapezoidPath(
    gfx: Graphics,
    cx: number,
    cy: number,
    totalWidth: number,
    totalHeight: number
  ): void {
    const halfH = totalHeight / 2;
    const bottomHalfW = totalWidth / 2;
    const topHalfW = bottomHalfW * STYLES.perspectiveRatio;
    const rBot = Math.min(
      totalHeight * STYLES.cornerRadiusFraction,
      topHalfW,
      halfH
    );
    const rTop = Math.min(
      rBot * STYLES.topCornerRadiusScale,
      topHalfW,
      halfH
    );

    const topLeft = { x: cx - topHalfW, y: cy - halfH };
    const topRight = { x: cx + topHalfW, y: cy - halfH };
    const bottomRight = { x: cx + bottomHalfW, y: cy + halfH };
    const bottomLeft = { x: cx - bottomHalfW, y: cy + halfH };

    // Side edge unit direction (TR → BR)
    const dw = bottomHalfW - topHalfW;
    const sideLen = Math.sqrt(dw * dw + totalHeight * totalHeight);
    const sdx = dw / sideLen;
    const sdy = totalHeight / sideLen;

    // Tangent lengths at each corner type
    const alphaTop = Math.acos(Math.max(-1, Math.min(1, -sdx)));
    const alphaBot = Math.acos(Math.max(-1, Math.min(1, sdx)));
    const tLenTop = rTop / Math.tan(alphaTop / 2);
    const tLenBot = rBot / Math.tan(alphaBot / 2);

    const sideBulge = totalWidth * STYLES.sideBulge;
    const vertBulge = totalHeight * STYLES.sideBulge;

    // Edge control points (for quadratic bulge)
    const bottomEdgeCtrl = { x: cx, y: bottomLeft.y + vertBulge };

    // Right side tangent points (after TR arc → before BR arc)
    const rightStart = {
      x: topRight.x + tLenTop * sdx,
      y: topRight.y + tLenTop * sdy,
    };
    const rightEnd = {
      x: bottomRight.x - tLenBot * sdx,
      y: bottomRight.y - tLenBot * sdy,
    };
    const rightCtrl = {
      x: (rightStart.x + rightEnd.x) / 2 + sideBulge * sdy,
      y: (rightStart.y + rightEnd.y) / 2 - sideBulge * sdx,
    };

    // Left side tangent points (after BL arc → before TL arc)
    const leftStart = {
      x: bottomLeft.x + tLenBot * sdx,
      y: bottomLeft.y - tLenBot * sdy,
    };
    const leftEnd = {
      x: topLeft.x - tLenTop * sdx,
      y: topLeft.y + tLenTop * sdy,
    };
    const leftCtrl = {
      x: (leftStart.x + leftEnd.x) / 2 - sideBulge * sdy,
      y: (leftStart.y + leftEnd.y) / 2 - sideBulge * sdx,
    };

    // Corner tangent points (where arcs start/end)
    const trIn = { x: topRight.x - tLenTop, y: topRight.y };
    const trOut = rightStart;
    const brIn = rightEnd;
    const brOut = { x: bottomRight.x - tLenBot, y: bottomRight.y };
    const blIn = { x: bottomLeft.x + tLenBot, y: bottomLeft.y };
    const blOut = leftStart;
    const tlIn = leftEnd;
    const tlOut = { x: topLeft.x + tLenTop, y: topLeft.y };

    // Build polyline: accumulate all points, then draw with moveTo + lineTo
    const points: Point2D[] = [];

    // Start at TL outgoing tangent (top of top edge)
    points.push(tlOut);

    // Top edge (flat)
    points.push(trIn);

    // TR corner arc
    addCornerArc(points, trIn, topRight, trOut, rTop);

    // Right edge (quadratic curve approximation)
    addQuadratic(points, trOut, rightCtrl, brIn);

    // BR corner arc
    addCornerArc(points, brIn, bottomRight, brOut, rBot);

    // Bottom edge (quadratic curve approximation)
    addQuadratic(points, brOut, bottomEdgeCtrl, blIn);

    // BL corner arc
    addCornerArc(points, blIn, bottomLeft, blOut, rBot);

    // Left edge (quadratic curve approximation)
    addQuadratic(points, blOut, leftCtrl, tlIn);

    // TL corner arc
    addCornerArc(points, tlIn, topLeft, tlOut, rTop);

    // Draw the polyline
    const firstPoint = points[0];
    if (!firstPoint) return;
    gfx.moveTo(firstPoint.x, firstPoint.y);
    for (let i = 1; i < points.length; i++) {
      const point = points[i];
      if (point) {
        gfx.lineTo(point.x, point.y);
      }
    }
    gfx.closePath();
  }

  private drawTrapezoidFill(
    cx: number,
    cy: number,
    totalWidth: number,
    totalHeight: number,
    color: number | { color: number; alpha: number }
  ): void {
    this.trapezoidPath(cx, cy, totalWidth, totalHeight);
    this.tableGraphics.fill(color);
  }

  /**
   * Draw a filled trapezoid on a specific Graphics object (used for felt mask).
   */
  private drawTrapezoidFillOn(
    gfx: Graphics,
    cx: number,
    cy: number,
    totalWidth: number,
    totalHeight: number,
    color: number
  ): void {
    this.buildTrapezoidPath(gfx, cx, cy, totalWidth, totalHeight);
    gfx.fill(color);
  }

  private drawTrapezoidStroke(
    cx: number,
    cy: number,
    totalWidth: number,
    totalHeight: number,
    color: number,
    width: number,
    alpha?: number
  ): void {
    this.trapezoidPath(cx, cy, totalWidth, totalHeight);
    const style: { width: number; color: number; alpha?: number } = { width, color };
    if (alpha !== undefined) {
      style.alpha = alpha;
    }
    this.tableGraphics.setStrokeStyle(style);
    this.tableGraphics.stroke();
  }
}
